import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowRight, GraduationCap, Award, Building2, Quote } from "lucide-react";
import lauraImg from "../assets/laura-figari.png";
import gerardoImg from "../assets/gerardo-benitez.png";
import rodrigoImg from "../assets/rodrigo-monges.png";
import joseImg from "../assets/jose-mereles.jpg";
import javierImg from "../assets/javier-godoy.jpg";
import alejandroImg from "../assets/alejandro-fernandez.png";
import sergioImg from "../assets/sergio-canete.png";

export const Route = createFileRoute("/trayectoria")({
  head: () => ({
    meta: [
      { title: "Socios — Estudio Libertad E.A.E.S. | Consultores y Abogados" },
      {
        name: "description",
        content:
          "Conozca a los socios del Estudio Libertad E.A.E.S.: Abg. Laura Figari, Abg. Gerardo Benítez, Abg. Rodrigo Monges Luces, Abg. José Mereles, Abg. Francisco Javier Godoy Moreno, Abg. Alejandro Daniel Fernández y Abg. Sergio Cañete. Práctica penal, procesal penal, civil, familia, comercial, societaria, sucesoria, laboral, inmobiliaria, notarial, convocatoria de acreedores y quiebras, juicios ejecutivos, procesos ordinarios, contencioso administrativa e indemnizatoria patrimonial en Asunción, Paraguay.",
      },
      { property: "og:title", content: "Socios — Estudio Libertad E.A.E.S." },
      {
        property: "og:description",
        content:
          "Siete socios — Figari, Benítez, Monges Luces, Mereles, Godoy Moreno, Fernández y Cañete — del Estudio Libertad en Asunción, Paraguay.",
      },
    ],
  }),
  component: TrayectoriaPage,
});

function TrayectoriaPage() {
  return (
    <>
      {/* HERO */}
      <section className="bg-navy-900 py-24 text-stone-100 md:py-32">
        <div className="container-elegant text-center">
          <span className="text-[10px] font-semibold tracking-[0.4em] uppercase text-gold-400">
            Los Socios · La Firma
          </span>
          <h1 className="mx-auto mt-6 max-w-3xl font-serif text-4xl leading-tight md:text-6xl">
            Estudio Libertad <span className="italic text-gold-400">E.A.E.S.</span>
          </h1>
          <div className="mx-auto mt-8 h-px w-16 bg-gold-500" />
          <p className="mx-auto mt-8 max-w-2xl text-stone-100/75">
            Una firma boutique conducida por siete socios, con prácticas complementarias en derecho
            penal, procesal penal, civil, comercial, societario, familia, niñez, sucesorio, laboral,
            inmobiliario, notarial, contencioso administrativo e indemnizatorio patrimonial.
          </p>
        </div>
      </section>

      {/* RODRIGO */}
      <section className="py-24 md:py-32">
        <div className="container-elegant grid gap-16 md:grid-cols-2 md:items-center">
          <div className="relative">
            <img
              src={rodrigoImg}
              alt="Abg. Rodrigo Monges Luces"
              width={1024}
              height={1280}
              loading="lazy"
              className="aspect-[3/4] w-full object-cover"
            />
            <div className="absolute -bottom-6 -left-6 hidden border border-gold-500/40 bg-stone-100 p-6 md:block">
              <span className="text-[10px] tracking-[0.3em] uppercase text-gold-500">Socio</span>
              <p className="mt-2 font-serif text-lg text-navy-900">Derecho Penal</p>
            </div>
          </div>

          <div>
            <span className="text-[10px] font-semibold tracking-[0.4em] uppercase text-gold-500">
              Socio
            </span>
            <h2 className="mt-6 font-serif text-4xl leading-tight text-navy-900 md:text-5xl">
              Abg. Rodrigo <span className="italic text-gold-500">Monges Luces</span>
            </h2>
            <div className="mt-8 h-px w-16 bg-gold-500" />
            <p className="mt-8 text-lg leading-relaxed text-navy-900/75">
              Especialista en derecho penal. Defensa estratégica en causas de alto perfil con
              atención directa, rigor técnico y absoluta confidencialidad.
            </p>
            <p className="mt-5 text-navy-900/65">
              Interviene personalmente en cada causa penal, desde la instrucción hasta el juicio
              oral, incluyendo recursos de apelación y casación ante la Corte Suprema de Justicia
              del Paraguay.
            </p>

            <ul className="mt-8 grid gap-3 text-sm text-navy-900/75">
              <PracticeBadge label="Derecho Penal General" />
              <PracticeBadge label="Penal Económico" />
              <PracticeBadge label="Litigación Compleja" />
              <PracticeBadge label="Asesoría Penal Preventiva" />
            </ul>
          </div>
        </div>
      </section>

      {/* LAURA */}
      <section className="bg-stone-100 py-24 md:py-32">
        <div className="container-elegant grid gap-16 md:grid-cols-2 md:items-center">
          <div className="relative">
            <img
              src={lauraImg}
              alt="Abg. Laura Figari"
              width={1024}
              height={1280}
              loading="lazy"
              className="aspect-[3/4] w-full object-cover"
            />
            <div className="absolute -bottom-6 -left-6 hidden border border-gold-500/40 bg-white p-6 md:block">
              <span className="text-[10px] tracking-[0.3em] uppercase text-gold-500">Socia</span>
              <p className="mt-2 font-serif text-lg text-navy-900">Civil · Familia</p>
            </div>
          </div>

          <div>
            <span className="text-[10px] font-semibold tracking-[0.4em] uppercase text-gold-500">
              Socia
            </span>
            <h2 className="mt-6 font-serif text-4xl leading-tight text-navy-900 md:text-5xl">
              Abg. Laura <span className="italic text-gold-500">Figari</span>
            </h2>
            <div className="mt-8 h-px w-16 bg-gold-500" />
            <p className="mt-8 text-lg leading-relaxed text-navy-900/75">
              Especialista en derecho civil, derecho de familia y niñez y adolescencia. Conduce los
              procesos más sensibles del estudio con rigor técnico y trato humano.
            </p>
            <p className="mt-5 text-navy-900/65">
              Acompaña a personas y familias en divorcios, regímenes patrimoniales, alimentos,
              filiación, tutela, adopción y demás procesos relacionados con la protección integral
              de la familia.
            </p>

            <ul className="mt-8 grid gap-3 text-sm text-navy-900/75">
              <PracticeBadge label="Derecho Civil" />
              <PracticeBadge label="Derecho de Familia" />
              <PracticeBadge label="Niñez y Adolescencia" />
            </ul>
          </div>
        </div>
      </section>

      {/* GERARDO */}
      <section className="py-24 md:py-32">
        <div className="container-elegant grid gap-16 md:grid-cols-2 md:items-center">
          <div className="md:order-2 relative">
            <img
              src={gerardoImg}
              alt="Abg. Gerardo Benítez"
              width={1024}
              height={1280}
              loading="lazy"
              className="aspect-[3/4] w-full object-cover"
            />
            <div className="absolute -bottom-6 -right-6 hidden border border-gold-500/40 bg-stone-100 p-6 md:block">
              <span className="text-[10px] tracking-[0.3em] uppercase text-gold-500">Socio</span>
              <p className="mt-2 font-serif text-lg text-navy-900">Civil · Comercial</p>
            </div>
          </div>

          <div className="md:order-1">
            <span className="text-[10px] font-semibold tracking-[0.4em] uppercase text-gold-500">
              Socio
            </span>
            <h2 className="mt-6 font-serif text-4xl leading-tight text-navy-900 md:text-5xl">
              Abg. Gerardo <span className="italic text-gold-500">Benítez</span>
            </h2>
            <div className="mt-8 h-px w-16 bg-gold-500" />
            <p className="mt-8 text-lg leading-relaxed text-navy-900/75">
              Especialista en derecho civil, comercial y societario, con foco en penal patrimonial e
              inversiones en valores.
            </p>
            <p className="mt-5 text-navy-900/65">
              Asesora a empresas, directivos e inversionistas en constitución y reorganización de
              sociedades, contratos comerciales, defensa penal patrimonial y estructuración de
              operaciones financieras.
            </p>

            <ul className="mt-8 grid gap-3 text-sm text-navy-900/75">
              <PracticeBadge label="Civil & Comercial" />
              <PracticeBadge label="Sociedades" />
              <PracticeBadge label="Penal Patrimonial" />
              <PracticeBadge label="Inversiones en Valores" />
            </ul>
          </div>
        </div>
      </section>

      {/* JOSE */}
      <section className="bg-stone-100 py-24 md:py-32">
        <div className="container-elegant grid gap-16 md:grid-cols-2 md:items-center">
          <div className="relative">
            <img
              src={joseImg}
              alt="Abg. José Mereles"
              width={1024}
              height={1280}
              loading="lazy"
              className="aspect-[3/4] w-full object-cover"
            />
            <div className="absolute -bottom-6 -left-6 hidden border border-gold-500/40 bg-white p-6 md:block">
              <span className="text-[10px] tracking-[0.3em] uppercase text-gold-500">Socio</span>
              <p className="mt-2 font-serif text-lg text-navy-900">Sucesorio · Notarial · Quiebras</p>
            </div>
          </div>

          <div>
            <span className="text-[10px] font-semibold tracking-[0.4em] uppercase text-gold-500">
              Socio
            </span>
            <h2 className="mt-6 font-serif text-4xl leading-tight text-navy-900 md:text-5xl">
              Abg. José <span className="italic text-gold-500">Mereles</span>
            </h2>
            <div className="mt-8 h-px w-16 bg-gold-500" />
            <p className="mt-8 text-lg leading-relaxed text-navy-900/75">
              Especialista en derecho sucesorio, civil, familia, laboral, inmobiliario, asesoría
              notarial y convocatoria de acreedores y quiebras. Acompaña a personas, familias y
              empresas con un enfoque cercano y resolutivo.
            </p>
            <p className="mt-5 text-navy-900/65">
              Lleva sucesiones testamentarias e intestadas, procesos civiles y de familia,
              liquidaciones laborales, operaciones inmobiliarias, trámites notariales y procesos
              de convocatoria de acreedores y quiebras vinculados a cada gestión.
            </p>

            <ul className="mt-8 grid gap-3 text-sm text-navy-900/75">
              <PracticeBadge label="Derecho Sucesorio" />
              <PracticeBadge label="Derecho Civil y Familia" />
              <PracticeBadge label="Derecho Laboral" />
              <PracticeBadge label="Derecho Inmobiliario" />
              <PracticeBadge label="Asesoría Notarial" />
              <PracticeBadge label="Convocatoria de Acreedores y Quiebras" />
            </ul>
          </div>
        </div>
      </section>

      {/* JAVIER */}
      <section className="py-24 md:py-32">
        <div className="container-elegant grid gap-16 md:grid-cols-2 md:items-center">
          <div className="md:order-2 relative">
            <img
              src={javierImg}
              alt="Abg. Francisco Javier Godoy Moreno"
              width={1024}
              height={1280}
              loading="lazy"
              className="aspect-[3/4] w-full object-cover"
            />
            <div className="absolute -bottom-6 -right-6 hidden border border-gold-500/40 bg-stone-100 p-6 md:block">
              <span className="text-[10px] tracking-[0.3em] uppercase text-gold-500">Socio</span>
              <p className="mt-2 font-serif text-lg text-navy-900">Penal · Administrativo</p>
            </div>
          </div>

          <div className="md:order-1">
            <span className="text-[10px] font-semibold tracking-[0.4em] uppercase text-gold-500">
              Socio
            </span>
            <h2 className="mt-6 font-serif text-4xl leading-tight text-navy-900 md:text-5xl">
              Abg. Francisco Javier <span className="italic text-gold-500">Godoy Moreno</span>
            </h2>
            <div className="mt-8 h-px w-16 bg-gold-500" />
            <p className="mt-8 text-lg leading-relaxed text-navy-900/75">
              Especialista en derecho penal, procesal penal y derecho administrativo. Defensa
              técnica con énfasis en el debido proceso y patrocinio frente a la Administración
              Pública.
            </p>
            <p className="mt-5 text-navy-900/65">
              Interviene en causas penales en todas sus etapas, en sumarios y sanciones
              administrativas y en litigios contencioso-administrativos ante el Tribunal de Cuentas
              y demás instancias.
            </p>

            <ul className="mt-8 grid gap-3 text-sm text-navy-900/75">
              <PracticeBadge label="Derecho Penal" />
              <PracticeBadge label="Derecho Procesal Penal" />
              <PracticeBadge label="Derecho Administrativo Sancionatorio" />
              <PracticeBadge label="Derecho Contencioso Administrativo" />
            </ul>
          </div>
        </div>
      </section>

      {/* ALEJANDRO */}
      <section className="bg-stone-100 py-24 md:py-32">
        <div className="container-elegant grid gap-16 md:grid-cols-2 md:items-center">
          <div className="relative">
            <img
              src={alejandroImg}
              alt="Abg. Alejandro Daniel Fernández"
              width={1024}
              height={1280}
              loading="lazy"
              className="aspect-[3/4] w-full object-cover"
            />
            <div className="absolute -bottom-6 -left-6 hidden border border-gold-500/40 bg-white p-6 md:block">
              <span className="text-[10px] tracking-[0.3em] uppercase text-gold-500">Socio</span>
              <p className="mt-2 font-serif text-lg text-navy-900">Penal · Civil · Comercial</p>
            </div>
          </div>

          <div>
            <span className="text-[10px] font-semibold tracking-[0.4em] uppercase text-gold-500">
              Socio
            </span>
            <h2 className="mt-6 font-serif text-4xl leading-tight text-navy-900 md:text-5xl">
              Abg. Alejandro Daniel <span className="italic text-gold-500">Fernández</span>
            </h2>
            <div className="mt-8 h-px w-16 bg-gold-500" />
            <p className="mt-8 text-lg leading-relaxed text-navy-900/75">
              Especialista en derecho penal, procesal penal, civil y comercial, societario e
              indemnizatorio patrimonial. Combina defensa técnica con asesoramiento corporativo.
            </p>
            <p className="mt-5 text-navy-900/65">
              Interviene en causas penales y procesales penales, conflictos civiles y comerciales,
              estructuración societaria y reclamos indemnizatorios patrimoniales, tanto para
              personas como para empresas.
            </p>

            <ul className="mt-8 grid gap-3 text-sm text-navy-900/75">
              <PracticeBadge label="Derecho Penal" />
              <PracticeBadge label="Derecho Procesal Penal" />
              <PracticeBadge label="Derecho Civil y Comercial" />
              <PracticeBadge label="Derecho Societario" />
              <PracticeBadge label="Indemnizatorio Patrimonial" />
            </ul>
          </div>
        </div>
      </section>

      {/* SERGIO */}
      <section className="py-24 md:py-32">
        <div className="container-elegant grid gap-16 md:grid-cols-2 md:items-center">
          <div className="md:order-2 relative">
            <img
              src={sergioImg}
              alt="Abg. Sergio Cañete"
              width={1024}
              height={1280}
              loading="lazy"
              className="aspect-[3/4] w-full object-cover"
            />
            <div className="absolute -bottom-6 -right-6 hidden border border-gold-500/40 bg-stone-100 p-6 md:block">
              <span className="text-[10px] tracking-[0.3em] uppercase text-gold-500">Socio</span>
              <p className="mt-2 font-serif text-lg text-navy-900">Penal · Ejecutivos · Laboral</p>
            </div>
          </div>

          <div className="md:order-1">
            <span className="text-[10px] font-semibold tracking-[0.4em] uppercase text-gold-500">
              Socio
            </span>
            <h2 className="mt-6 font-serif text-4xl leading-tight text-navy-900 md:text-5xl">
              Abg. Sergio <span className="italic text-gold-500">Cañete</span>
            </h2>
            <div className="mt-8 h-px w-16 bg-gold-500" />
            <p className="mt-8 text-lg leading-relaxed text-navy-900/75">
              Especialista en derecho penal y procesal penal, juicios ejecutivos, procesos
              ordinarios y demandas laborales. Combina defensa técnica con litigación civil y
              laboral para personas y empresas.
            </p>
            <p className="mt-5 text-navy-900/65">
              Interviene en causas penales en todas sus etapas, juicios ejecutivos para el cobro de
              títulos de crédito, procesos ordinarios civiles y patrocinio en demandas laborales
              ante el fuero del trabajo.
            </p>

            <ul className="mt-8 grid gap-3 text-sm text-navy-900/75">
              <PracticeBadge label="Derecho Penal" />
              <PracticeBadge label="Derecho Procesal Penal" />
              <PracticeBadge label="Juicios Ejecutivos" />
              <PracticeBadge label="Procesos Ordinarios" />
              <PracticeBadge label="Demandas Laborales" />
            </ul>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="py-24 md:py-32">
        <div className="container-elegant">
          <div className="mx-auto mb-20 max-w-2xl text-center">
            <span className="text-[10px] font-semibold tracking-[0.4em] uppercase text-gold-500">
              Pilares Profesionales
            </span>
            <h2 className="mt-4 font-serif text-4xl text-navy-900 md:text-5xl">
              Una práctica con identidad propia
            </h2>
            <div className="mx-auto mt-6 h-px w-16 bg-gold-500" />
          </div>

          <div className="grid gap-1 bg-navy-900/10 md:grid-cols-3">
            {[
              {
                icon: GraduationCap,
                title: "Formación Sólida",
                text: "Trayectoria académica y permanente actualización en cada una de nuestras áreas de práctica.",
              },
              {
                icon: Building2,
                title: "Estudio Propio",
                text: "Estructura formal en Asunción con equipo dedicado y procesos consolidados de trabajo.",
              },
              {
                icon: Award,
                title: "Resultados Demostrables",
                text: "Historial de causas resueltas con éxito y la confianza renovada de empresas y particulares.",
              },
            ].map(({ icon: Icon, title, text }) => (
              <div key={title} className="bg-stone-100 p-10 md:p-12">
                <Icon className="h-7 w-7 text-gold-500" />
                <h3 className="mt-8 font-serif text-2xl text-navy-900">{title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-navy-900/65">{text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PHILOSOPHY */}
      <section className="bg-stone-100 py-24 md:py-32">
        <div className="container-elegant max-w-4xl">
          <Quote className="h-10 w-10 text-gold-500" />
          <blockquote className="mt-8 font-serif text-3xl leading-snug text-navy-900 md:text-4xl">
            "El ejercicio del derecho exige técnica, estrategia y un compromiso humano absoluto con
            quien deposita su confianza. Nada más, nada menos."
          </blockquote>
          <p className="mt-8 text-[10px] font-semibold tracking-[0.3em] uppercase text-gold-500">
            — Estudio Libertad E.A.E.S.
          </p>
        </div>
      </section>

      {/* APPROACH */}
      <section className="py-24 md:py-32">
        <div className="container-elegant grid gap-16 md:grid-cols-2">
          <div>
            <span className="text-[10px] font-semibold tracking-[0.4em] uppercase text-gold-500">
              Nuestro Enfoque
            </span>
            <h2 className="mt-4 font-serif text-3xl text-navy-900 md:text-4xl">
              Estrategia, atención personal y trabajo en equipo
            </h2>
            <div className="mt-6 h-px w-16 bg-gold-500" />
          </div>
          <div className="space-y-8 text-navy-900/75">
            <div>
              <h3 className="font-serif text-xl text-navy-900">Atención directa</h3>
              <p className="mt-2 text-sm leading-relaxed">
                Cada caso es atendido personalmente por uno de los socios, asegurando criterio
                técnico y compromiso desde el primer contacto.
              </p>
            </div>
            <div>
              <h3 className="font-serif text-xl text-navy-900">Áreas complementarias</h3>
              <p className="mt-2 text-sm leading-relaxed">
                La combinación civil/familia y comercial/societario/penal patrimonial nos permite
                acompañar a personas, familias, empresas e inversionistas con el mismo equipo.
              </p>
            </div>
            <div>
              <h3 className="font-serif text-xl text-navy-900">Compromisos éticos firmes</h3>
              <p className="mt-2 text-sm leading-relaxed">
                No tomamos casos vinculados al narcotráfico ni al lavado de activos derivado de
                éste. La integridad es innegociable.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-navy-950 py-24 text-stone-100">
        <div className="container-elegant text-center">
          <h2 className="mx-auto max-w-2xl font-serif text-3xl md:text-4xl">
            Conversemos sobre su caso en una consulta privada.
          </h2>
          <Link
            to="/contacto"
            className="mt-10 inline-flex items-center gap-3 bg-gold-500 px-10 py-5 text-[11px] font-semibold tracking-[0.2em] uppercase text-white transition-transform hover:scale-[1.02]"
          >
            Agendar Consulta
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>
    </>
  );
}

function PracticeBadge({ label }: { label: string }) {
  return (
    <li className="flex items-center gap-3">
      <span className="h-px w-6 bg-gold-500" />
      <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-navy-900">
        {label}
      </span>
    </li>
  );
}
